import { Button, type ButtonProps, Drawer, Stack, Link } from '@mui/material'
import React, { useState } from 'react'

import Icon from 'components/Icon'
import { type ThirdPartyExternalIntegration } from 'components/rbac-settings/teams/integrations/third-party/third-party-account-integrations-utils'
import { ThirdPartyAuth } from 'components/rbac-settings/teams/integrations/third-party/ThirdPartyAuth'
import { ThirdPartySelectMenu } from 'components/rbac-settings/teams/integrations/third-party/ThirdPartySelectMenu'
import { type TeamExternalIntegrationRow } from 'components/status-checks/common/team-integration-table/team-integration-table-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { usePopoverState } from 'hooks/use-popover-state'
import {
  ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'

const DEFAULT_OPTION = ObservabilityToolType.AWS

type ThirdPartyAccountDrawerProps = {
  buttonProps?: Omit<ButtonProps, 'children' | 'onClick'>
  externalIntegrations: TeamExternalIntegration[]
  mode?: 'add' | 'edit' | 'read-only'
  selectedIntegration?: TeamExternalIntegrationRow
}

export function ThirdPartyAccountDrawer({
  buttonProps,
  externalIntegrations,
  mode = 'add',
  selectedIntegration
}: ThirdPartyAccountDrawerProps) {
  const { setIsEdit } = useObservabilityToolAuthenticationContext()
  const drawerState = usePopoverState()

  const [integrationTypeSelection, setIntegrationTypeSelection] =
    useState<ThirdPartyExternalIntegration>(DEFAULT_OPTION)

  /*
   * In edit mode, selectedIntegration must be passed in.
   */
  const isEdit = mode === 'edit' && selectedIntegration !== undefined

  const handleOpen = () => {
    drawerState.open()
    setIsEdit(isEdit)
  }

  const handleClose = () => {
    drawerState.close()
    setIsEdit(false)
  }

  /*
   * In read-only mode, selectedIntegration must be passed in.
   * The displayName property is made available in TeamIntegrationTable.
   */
  const displayName =
    mode === 'read-only' && selectedIntegration
      ? selectedIntegration.displayName
      : undefined

  return (
    <>
      {displayName ? (
        <Link
          component="button"
          onClick={(event) => {
            event.preventDefault()
            handleOpen()
          }}
        >
          {displayName}
        </Link>
      ) : (
        <Button
          size="small"
          startIcon={<Icon icon={mode === 'add' ? 'plus' : 'edit'} />}
          variant="contained"
          {...buttonProps}
          onClick={handleOpen}
        >
          {mode === 'add' ? 'ID' : 'Edit'}
        </Button>
      )}
      <Drawer
        anchor="right"
        open={drawerState.isOpen}
        PaperProps={{ sx: { width: 'min(1200px, 90%)' } }}
        onClose={handleClose}
      >
        <Stack sx={{ padding: 3 }}>
          {mode === 'add' && (
            <ThirdPartySelectMenu
              selected={integrationTypeSelection}
              onSelect={setIntegrationTypeSelection}
            />
          )}
          <ThirdPartyAuth
            externalIntegrations={externalIntegrations}
            selectedIntegration={selectedIntegration}
            type={
              selectedIntegration?.observabilityToolType ??
              integrationTypeSelection
            }
            onClose={handleClose}
          />
        </Stack>
      </Drawer>
    </>
  )
}
